import {
	ChevronDown,
	ChevronRight,
	Hash,
	MessageSquare,
	Mic,
	MicOff,
	MoreHorizontal,
	PhoneForwarded,
	PhoneOff,
	ShieldOff,
} from 'lucide-react';
import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	type ComponentType,
	type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ActionSheet, type ActionSheetItem } from '@/components/mobile';
import { cn } from '@/lib/utils';
import { setFullScreenCallShown } from './fullScreenCall';
import { keepPageDialogsOpen } from './useCompactCallUi';

const KEYPAD_KEYS: [string, string][] = [
	['1', ''],
	['2', 'ABC'],
	['3', 'DEF'],
	['4', 'GHI'],
	['5', 'JKL'],
	['6', 'MNO'],
	['7', 'PQRS'],
	['8', 'TUV'],
	['9', 'WXYZ'],
	['*', ''],
	['0', '+'],
	['#', ''],
];

export type CallTone = 'live' | 'forwarding' | 'ended';

export interface PhoneCallScreenProps {
	/** Full-screen call screen; false shows the bar at the top instead. */
	expanded: boolean;
	onExpandedChange: (expanded: boolean) => void;
	displayName: string;
	/** Already formatted for display. */
	displayPhone?: string;
	initials: string;
	statusText: string;
	statusTextClassName: string;
	statusDotClassName: string;
	/** What the bar shows after the name: the timer once connected, else the status. */
	barStatus: string;
	duration: string;
	tone: CallTone;
	isCustomerSpeaking: boolean;
	/** Extra explanation under the status, e.g. the recording notice. */
	note?: ReactNode;
	/** 0-100 while the ended call counts down to closing; null otherwise. */
	dismissProgress: number | null;
	isMuted: boolean;
	canMute: boolean;
	onToggleMute: () => void;
	canUseKeypad: boolean;
	isKeypadOpen: boolean;
	keypadDigits: string;
	onOpenKeypad: () => void;
	onCloseKeypad: () => void;
	onPressDigit: (digit: string) => void;
	canForward: boolean;
	isForwardPanelOpen: boolean;
	onForward: () => void;
	/** Opens the customer's conversation; absent when there is none. */
	onViewConversation?: () => void;
	/** Opens the block confirmation; absent without a customer record. */
	onBlock?: () => void;
	canEndCall: boolean;
	endCallLabel: string;
	onEndCall: () => void;
	/** A dialog over the call screen (forward, block) owns Escape. */
	isOverlayOpen: boolean;
}

/**
 * The call UI on phones and short touch screens. The full-screen call screen
 * has the controls as large round buttons; minimising it leaves a bar at the
 * top of the screen (like iOS's "touch to return to call") that expands it
 * again. While the bar shows, `--call-bar-height` on <html> holds its height
 * below the top safe area, so the app shell can push the page down instead of
 * hiding its header under the bar. On a phone in landscape the same bar spans
 * the top and the shell moves the sidebar and the page below it, so the bar
 * never sits over a page's header actions.
 */
export function PhoneCallScreen({
	expanded,
	onExpandedChange,
	displayName,
	displayPhone,
	initials,
	statusText,
	statusTextClassName,
	statusDotClassName,
	barStatus,
	duration,
	tone,
	isCustomerSpeaking,
	note,
	dismissProgress,
	isMuted,
	canMute,
	onToggleMute,
	canUseKeypad,
	isKeypadOpen,
	keypadDigits,
	onOpenKeypad,
	onCloseKeypad,
	onPressDigit,
	canForward,
	isForwardPanelOpen,
	onForward,
	onViewConversation,
	onBlock,
	canEndCall,
	endCallLabel,
	onEndCall,
	isOverlayOpen,
}: PhoneCallScreenProps) {
	const barRowRef = useRef<HTMLDivElement>(null);
	const barButtonRef = useRef<HTMLButtonElement>(null);
	const sheetRef = useRef<HTMLDivElement>(null);
	const topRowRef = useRef<HTMLDivElement>(null);
	const minimizeButtonRef = useRef<HTMLButtonElement>(null);
	const callerSectionRef = useRef<HTMLElement>(null);
	const wasExpandedRef = useRef(expanded);
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	// Publish the bar's height so the shell can make room for it. Measured,
	// so larger text settings still push the page down far enough.
	useLayoutEffect(() => {
		const root = document.documentElement;
		const row = barRowRef.current;
		if (expanded || !row) {
			root.style.setProperty('--call-bar-height', '0px');
			return;
		}
		const update = () => root.style.setProperty('--call-bar-height', `${row.offsetHeight}px`);
		update();
		const observer = new ResizeObserver(update);
		observer.observe(row);
		return () => observer.disconnect();
	}, [expanded]);

	useEffect(
		() => () => {
			document.documentElement.style.removeProperty('--call-bar-height');
		},
		[],
	);

	// While the call screen is full screen, publish where the Minimize
	// button ends (`--call-screen-minimize-width`) and where the caller
	// column ends (`--call-screen-caller-width`, the left column in
	// landscape), both from the screen's left edge with the safe area
	// included. A landscape phone's toasts sit between the two, clear of
	// the controls and the keypad (see Notifications).
	useLayoutEffect(() => {
		const root = document.documentElement;
		const row = topRowRef.current;
		const minimizeButton = minimizeButtonRef.current;
		const callerSection = callerSectionRef.current;
		if (!expanded || !row || !minimizeButton || !callerSection) return;
		const update = () => {
			root.style.setProperty('--call-screen-minimize-width', `${Math.ceil(minimizeButton.getBoundingClientRect().right)}px`);
			root.style.setProperty('--call-screen-caller-width', `${Math.floor(callerSection.getBoundingClientRect().right)}px`);
		};
		update();
		// The row resizes with the screen, the safe area and the text size;
		// the caller column also narrows when the controls need more room
		// than the keypad.
		const observer = new ResizeObserver(update);
		observer.observe(row);
		observer.observe(callerSection);
		return () => {
			observer.disconnect();
			root.style.removeProperty('--call-screen-minimize-width');
			root.style.removeProperty('--call-screen-caller-width');
		};
	}, [expanded]);

	// And that it shows at all, for where the toasts go over it.
	useLayoutEffect(() => {
		if (!expanded) return;
		setFullScreenCallShown(true);
		return () => setFullScreenCallShown(false);
	}, [expanded]);

	// Move focus into the call screen when it opens and back to the bar when
	// it is minimised, so keyboard and VoiceOver users follow the change.
	useEffect(() => {
		if (expanded) sheetRef.current?.focus({ preventScroll: true });
		else if (wasExpandedRef.current) barButtonRef.current?.focus({ preventScroll: true });
		wasExpandedRef.current = expanded;
	}, [expanded]);

	useEffect(() => {
		if (!expanded || isOverlayOpen || isMenuOpen) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape' || event.defaultPrevented) return;
			if (isKeypadOpen) onCloseKeypad();
			else onExpandedChange(false);
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [expanded, isOverlayOpen, isMenuOpen, isKeypadOpen, onCloseKeypad, onExpandedChange]);

	const menuItems: ActionSheetItem[] = [
		...(onViewConversation
			? [{ id: 'conversation', label: 'View conversation', icon: MessageSquare, onSelect: onViewConversation }]
			: []),
		...(onBlock
			? [{ id: 'block', label: 'Block caller', icon: ShieldOff, destructive: true, onSelect: onBlock }]
			: []),
	];

	const progressBar = dismissProgress !== null && (
		<div aria-hidden className="absolute inset-x-0 top-0 z-10 h-1 bg-white/10">
			<div className="h-full bg-emerald-400 transition-[width] duration-75 ease-linear" style={{ width: `${dismissProgress}%` }} />
		</div>
	);

	if (!expanded) {
		return createPortal(
			<div
				onPointerDown={keepPageDialogsOpen}
				className={cn(
					'pointer-events-auto fixed inset-x-0 top-0 z-[60] select-none pl-safe pr-safe pt-safe text-white shadow-md shadow-black/10',
					// Landscape phones keep the desktop dialogs and menus, which
					// don't make room for the bar, so there it sits under them
					// like the rest of the shell.
					'md:z-40',
					'duration-300 animate-in slide-in-from-top motion-reduce:animate-none',
					tone === 'live' ? 'bg-emerald-700' : tone === 'forwarding' ? 'bg-blue-600' : 'bg-zinc-600',
				)}
			>
				{/* A phone in landscape with the keyboard up has 130-180px to show
				    (narrow ones like a 667px iPhone SE included), so there the bar
				    slims to a 28px strip, like iOS's call pill, and the shell
				    follows the measured --call-bar-height. The buttons fill
				    the strip (End stays 44px wide) but no hit area spills onto the
				    page header right below it, where a stray tap would end the call. */}
				<div
					ref={barRowRef}
					className="relative flex min-h-11 items-center gap-1 pl-4 pr-1 kb-open:max-xl:[@media(orientation:landscape)_and_(max-height:560px)]:min-h-7"
				>
					<button
						ref={barButtonRef}
						type="button"
						onClick={() => onExpandedChange(true)}
						aria-label={`Return to call with ${displayName}, ${statusText}`}
						className="flex min-h-11 min-w-0 flex-1 items-center gap-2 py-1.5 text-left outline-none focus-visible:underline kb-open:max-xl:[@media(orientation:landscape)_and_(max-height:560px)]:min-h-7 kb-open:max-xl:[@media(orientation:landscape)_and_(max-height:560px)]:py-0.5"
					>
						<span aria-hidden className="relative flex size-2.5 shrink-0">
							{tone !== 'ended' && <span className="absolute inset-0 animate-ping rounded-full bg-white/70 motion-reduce:animate-none" />}
							<span className="relative size-2.5 rounded-full bg-white" />
						</span>
						<span className="min-w-0 truncate text-[15px] font-semibold">{displayName}</span>
						{/* Capped so a long status can't squeeze the name out. */}
						<span className="max-w-[50%] shrink-0 truncate text-[15px] tabular-nums text-white">{barStatus}</span>
						<ChevronDown aria-hidden className="ml-auto size-5 shrink-0 rotate-180 text-white/80" />
					</button>
					{canEndCall && (
						<button
							type="button"
							onClick={onEndCall}
							aria-label="End call"
							className="flex size-11 shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-white kb-open:max-xl:[@media(orientation:landscape)_and_(max-height:560px)]:h-7"
						>
							<span className="flex size-8 items-center justify-center rounded-full bg-red-600 shadow-sm active:bg-red-700 kb-open:max-xl:[@media(orientation:landscape)_and_(max-height:560px)]:size-6">
								<PhoneOff aria-hidden className="size-4 kb-open:max-xl:[@media(orientation:landscape)_and_(max-height:560px)]:size-3.5" />
							</span>
						</button>
					)}
					{dismissProgress !== null && (
						<div aria-hidden className="absolute inset-x-0 bottom-0 h-0.5 bg-black/10">
							<div className="h-full bg-white/70" style={{ width: `${dismissProgress}%` }} />
						</div>
					)}
				</div>
			</div>,
			document.body,
		);
	}

	const endButton = (
		<button
			type="button"
			onClick={onEndCall}
			disabled={!canEndCall}
			aria-label={canEndCall ? 'End call' : endCallLabel}
			className="group flex w-20 flex-col items-center gap-1.5 rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-white/70"
		>
			<span
				className={cn(
					'flex size-[4.5rem] items-center justify-center rounded-full transition-colors landscape:size-14 [@media(orientation:portrait)_and_(max-height:600px)]:size-16',
					canEndCall ? 'bg-red-600 shadow-lg shadow-red-600/30 group-active:bg-red-700' : 'bg-white/10 text-white/50',
				)}
			>
				<PhoneOff aria-hidden className="size-8 landscape:size-6" />
			</span>
			<span className={cn('text-[13px] font-medium leading-tight', canEndCall ? 'text-white' : 'text-white/50')}>
				{canEndCall ? 'End' : endCallLabel}
			</span>
		</button>
	);

	return createPortal(
		<div
			ref={sheetRef}
			role="dialog"
			aria-modal="true"
			aria-label={`Call with ${displayName}`}
			tabIndex={-1}
			onPointerDown={keepPageDialogsOpen}
			className={cn(
				'pointer-events-auto fixed inset-x-0 top-0 z-50 flex h-app select-none flex-col overflow-hidden bg-gradient-to-b from-gray-900 via-gray-950 to-black pb-safe pl-safe pr-safe pt-safe text-white outline-none',
				'duration-300 animate-in slide-in-from-bottom motion-reduce:animate-none',
			)}
		>
			{progressBar}

			<div ref={topRowRef} className="flex h-12 shrink-0 items-center justify-between px-2 landscape:h-11">
				<button
					ref={minimizeButtonRef}
					type="button"
					onClick={() => onExpandedChange(false)}
					aria-label="Minimize call"
					className="flex size-11 items-center justify-center rounded-full text-white/90 outline-none active:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/70"
				>
					<ChevronDown aria-hidden className="size-7" />
				</button>
				{menuItems.length > 0 && (
					<button
						type="button"
						onClick={() => setIsMenuOpen(true)}
						aria-label="More options"
						aria-haspopup="dialog"
						aria-expanded={isMenuOpen}
						className="flex size-11 items-center justify-center rounded-full text-white/90 outline-none active:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/70"
					>
						<MoreHorizontal aria-hidden className="size-6" />
					</button>
				)}
			</div>

			<div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain landscape:flex-row">
				{/* Who the call is with and how it is going */}
				<section
					ref={callerSectionRef}
					className="flex flex-1 flex-col items-center justify-center px-6 pb-4 text-center landscape:min-w-0 landscape:pb-3"
				>
					{!isKeypadOpen && (
						<div className="relative mb-4 shrink-0 landscape:mb-2 [@media(orientation:portrait)_and_(max-height:600px)]:mb-2">
							<div
								className={cn(
									'flex size-24 items-center justify-center rounded-full border-2 bg-gray-800 transition-all duration-300 landscape:size-14 [@media(orientation:portrait)_and_(max-height:600px)]:size-16',
									isCustomerSpeaking ? 'border-green-500 shadow-lg shadow-green-500/50' : 'border-gray-700',
								)}
							>
								<span className="text-3xl font-semibold landscape:text-lg [@media(orientation:portrait)_and_(max-height:600px)]:text-xl">
									{initials}
								</span>
							</div>
							{isCustomerSpeaking && (
								<div className="absolute inset-0 animate-ping rounded-full border-2 border-green-400 opacity-75 motion-reduce:animate-none" />
							)}
						</div>
					)}
					<h2
						className={cn(
							'w-full max-w-sm truncate font-semibold leading-tight',
							isKeypadOpen ? 'text-lg text-white/80' : 'text-[28px] landscape:text-2xl',
						)}
					>
						{displayName}
					</h2>
					{isKeypadOpen ? (
						<p className="mt-1 h-11 w-full max-w-sm truncate font-mono text-4xl tabular-nums tracking-wider"
						>
							{keypadDigits || ' '}
						</p>
					) : (
						displayPhone && <p className="mt-1 w-full max-w-sm truncate text-[17px] text-gray-400">{displayPhone}</p>
					)}
					<div className="mt-2 flex items-center gap-2 text-[15px]">
						<span aria-hidden className={cn('size-2 shrink-0 rounded-full', statusDotClassName)} />
						<span role="status" aria-live="polite" className={cn('font-medium', statusTextClassName)}>
							{statusText}
						</span>
						<span aria-hidden className="text-white/30">
							·
						</span>
						<span className="tabular-nums text-white/90">{duration}</span>
					</div>
					{note && !isKeypadOpen && <p className="mt-2 max-w-xs text-[13px] leading-snug text-gray-400">{note}</p>}
					{onViewConversation && !isKeypadOpen && (
						<button
							type="button"
							onClick={onViewConversation}
							className="mt-4 inline-flex min-h-11 items-center gap-1 rounded-full bg-white/10 pl-4 pr-3 text-[15px] font-medium text-white outline-none active:bg-white/20 focus-visible:ring-2 focus-visible:ring-white/70 landscape:mt-3"
						>
							View conversation
							<ChevronRight aria-hidden className="size-4 text-white/70" />
						</button>
					)}
				</section>

				{/* Controls, or the keypad in their place */}
				<section className="flex shrink-0 flex-col items-center justify-end gap-6 px-6 pb-6 pt-2 landscape:flex-1 landscape:justify-center landscape:gap-2 landscape:pb-3 [@media(orientation:portrait)_and_(max-height:600px)]:gap-4 [@media(orientation:portrait)_and_(max-height:600px)]:pb-4">
					{isKeypadOpen ? (
						<div role="group" aria-label="Keypad" className="grid grid-cols-3 gap-x-6 gap-y-3 landscape:gap-x-5 landscape:gap-y-1.5 [@media(orientation:portrait)_and_(max-height:740px)]:gap-y-2">
							{KEYPAD_KEYS.map(([digit, letters]) => (
								<button
									key={digit}
									type="button"
									onClick={() => onPressDigit(digit)}
									aria-label={`Send digit ${digit}`}
									className={cn(
										'flex size-[4.5rem] flex-col items-center justify-center rounded-full bg-white/[0.14] text-white outline-none transition-colors active:bg-white/40 focus-visible:ring-2 focus-visible:ring-white/70',
										'landscape:size-12 [@media(orientation:portrait)_and_(min-height:601px)_and_(max-height:740px)]:size-16 [@media(orientation:portrait)_and_(max-height:600px)]:size-14',
									)}
								>
									<span className="text-[30px] font-normal leading-none landscape:text-xl">{digit}</span>
									{letters && (
										<span className="mt-0.5 text-[10px] font-semibold tracking-[0.15em] text-white/70 landscape:hidden">
											{letters}
										</span>
									)}
								</button>
							))}
						</div>
					) : (
						<div className="flex items-start justify-center gap-4">
							<RoundControl
								icon={isMuted ? MicOff : Mic}
								label="Mute"
								onClick={onToggleMute}
								disabled={!canMute}
								active={isMuted}
								pressed={isMuted}
							/>
							<RoundControl
								icon={Hash}
								label="Keypad"
								onClick={onOpenKeypad}
								disabled={!canUseKeypad}
								hasPopup
								expanded={isKeypadOpen}
							/>
							<RoundControl
								icon={PhoneForwarded}
								label="Forward"
								ariaLabel="Forward call"
								onClick={onForward}
								disabled={!canForward}
								hasPopup
								expanded={isForwardPanelOpen}
							/>
						</div>
					)}

					<div className="grid w-full max-w-[17rem] grid-cols-3 items-center justify-items-center">
						<span aria-hidden />
						{endButton}
						{isKeypadOpen ? (
							<button
								type="button"
								onClick={onCloseKeypad}
								className="min-h-11 rounded-full px-3 text-[17px] font-medium text-white outline-none active:opacity-60 focus-visible:ring-2 focus-visible:ring-white/70"
							>
								Hide
							</button>
						) : (
							<span aria-hidden />
						)}
					</div>
				</section>
			</div>

			<ActionSheet
				open={isMenuOpen}
				onOpenChange={setIsMenuOpen}
				title={displayName}
				items={menuItems}
			/>
		</div>,
		document.body,
	);
}

interface RoundControlProps {
	icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
	label: string;
	/** Defaults to the visible label. */
	ariaLabel?: string;
	onClick: () => void;
	disabled?: boolean;
	/** Filled white, like an iOS toggle that is on. */
	active?: boolean;
	pressed?: boolean;
	hasPopup?: boolean;
	expanded?: boolean;
}

function RoundControl({ icon: Icon, label, ariaLabel, onClick, disabled, active, pressed, hasPopup, expanded }: RoundControlProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			aria-label={ariaLabel}
			aria-pressed={pressed}
			aria-haspopup={hasPopup ? 'dialog' : undefined}
			aria-expanded={hasPopup ? expanded : undefined}
			className="group flex w-20 flex-col items-center gap-1.5 rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-white/70 disabled:opacity-35"
		>
			<span
				className={cn(
					'flex size-16 items-center justify-center rounded-full transition-colors landscape:size-14',
					active ? 'bg-white text-gray-950' : 'bg-white/[0.14] text-white group-active:bg-white/30',
				)}
			>
				<Icon aria-hidden className="size-7 landscape:size-6" />
			</span>
			<span className="text-[13px] font-medium leading-tight text-white">{label}</span>
		</button>
	);
}
